import { isNotEmpty } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import AutoResizeInput from 'react-input-autosize';
import styled from 'styled-components';

import { ButtonClose } from '../../ButtonClose/ButtonClose';
import { QuestionCircle } from '../../CircleText/CircleText';
import { colorsV2 } from '../../colors-v2';
import { QuestionV2 } from '../../Question/QuestionV2';

const EmptyDropBox = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colorsV2.gray80};

  .question-circle {
    background-color: ${colorsV2.gray10};
    border: 1px dashed ${colorsV2.gray60};
    margin-right: 8px;
  }
`;

export interface BlankChange {
  text: string;
  blankId: string;
}

interface BlankProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  blankId?: string;
  no: number;
  value?: string;
  onChange?: (input: any) => void;
  onRemoveBlank?: (changes: BlankChange) => void;
  disabled?: boolean;
  showRemoveButton?: boolean;
  showDropZone?: boolean;
  onClick?: any;
  input?: React.ReactNode | any;
  showQuestionNo?: boolean;
}

const getEmptyPlaceholder = (length: number) => {
  let s = '';
  for (let i = 0; i < length; i += 1) {
    s = `${s} `;
  }
  return s;
};

const Blank = (props: BlankProps) => {
  const {
    id,
    no,
    value,
    onChange = noop,
    onRemoveBlank = noop,
    onClick = noop,
    disabled,
    blankId,
    showRemoveButton = true,
    showDropZone = false,
    className,
    showQuestionNo = true,
    ...rest
  } = props;
  const inputRef = React.useRef<any>();
  const [inputValue, setInputValue] = React.useState(value);
  const [boxFocus, setBoxFocus] = React.useState(false);

  const hasValue = isNotEmpty(inputValue);
  const handleInputFocus = () => {
    setBoxFocus(true);
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  return (
    <Container
      onClick={(e) => {
        onClick(blankId, id);
      }}
      contentEditable={false}
      id={`question-no-${no}`}
      {...rest}
      className={cl(className, { 'has-value': hasValue, focused: boxFocus })}
    >
      {showQuestionNo && <QuestionV2 no={no} marginRight={0} />}

      {showDropZone && !hasValue && (
        <EmptyDropBox className="empty-drop-box">
          <QuestionCircle className="question-circle" />
          <span>Drop here</span>
        </EmptyDropBox>
      )}

      {!(showDropZone && !hasValue) && (
        <div
          contentEditable={false}
          onMouseDown={(e) => {
            e.preventDefault();
            handleInputFocus();
          }}
        >
          <AutoResizeInputStyled
            ref={inputRef}
            placeholder={getEmptyPlaceholder(12)}
            placeholderIsMinWidth
            name="blank-input"
            autoComplete="off"
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setInputValue(e.target.value);
              onChange({
                text: e.target.value,
                blankId: id,
              });
            }}
            onBlur={() => setBoxFocus(false)}
            disabled={disabled}
          />
        </div>
      )}

      {showRemoveButton && hasValue && (
        <ButtonCloseStyled
          onClick={(e) => {
            e.stopPropagation();
            setInputValue('');
            onRemoveBlank({ blankId, text: null, key: null });
          }}
        />
      )}
    </Container>
  );
};

export const ButtonCloseStyled = styled(ButtonClose)``;

export const BLANK_INPUT_MIN_WIDTH = 53;

export const AutoResizeInputStyled = styled(AutoResizeInput)`
  input {
    background-color: transparent;
    padding: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    outline: none;
    width: auto;
    color: ${colorsV2.black100};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    min-width: ${BLANK_INPUT_MIN_WIDTH}px;
    border-bottom: 2px solid ${colorsV2.black10};
    &:focus {
      border-bottom: 2px solid ${colorsV2.blue80};
    }
    &::selection {
      background: ${colorsV2.blue40};
      color: ${colorsV2.black100};
    }
  }
`;

const Container = styled.span`
  user-select: none;
  display: inline-grid;
  align-items: center;
  column-gap: 10px;
  grid-template-columns: max-content;
  grid-auto-flow: column;
  transition: 300ms;
  margin-right: 8px;
  margin-left: 8px;

  .question-circle {
    background-color: ${colorsV2.black80};
    border-radius: 50%;
    > span {
      color: ${colorsV2.white100} !important;
    }
  }

  &.has-value {
    .question-circle {
      background-color: ${colorsV2.blue100};
    }
  }

  &.focused {
    .question-circle {
      background-color: ${colorsV2.blue100};
    }
  }

  &:hover:not(.focused) {
    input[name='blank-input'] {
      box-shadow: none;
      border-radius: 0;
      border-bottom: 2px solid ${colorsV2.black10};
      &:focus {
        border-bottom: 2px solid ${colorsV2.blue80};
      }
    }
    ${ButtonCloseStyled} {
      color: ${colorsV2.black100};
      background-color: ${colorsV2.gray20};
    }
  }
`;

export default React.memo(Blank);
